// src/pages/SharedTournamentPage.jsx
// What participants see through the share link: the ranking (with the podium) and the rounds, live and read-only.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Podium from '../components/ranking/Podium';
import RankingTable from '../components/ranking/RankingTable';
import { TrophyIcon } from '../components/icons';
import { formatDateRange } from '../utils/time';
import { hasResults, ScoreHelp } from './tournament/RankingPanel';
import ReadOnlyRounds from './shared/ReadOnlyRounds';
import MyGames, { PlayerPicker } from './shared/MyGames';
import { rememberPlayer, rememberedPlayer } from './shared/findMe';
import { useSharedTournament } from './shared/useSharedTournament';

const clock = (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function SharedTournamentPage() {
    const { token } = useParams();
    const { data, status, fetchedAt } = useSharedTournament(token);
    const [view, setView] = useState('ranking'); // 'ranking' | 'rounds'
    const [meId, setMeId] = useState(() => rememberedPlayer(token));
    const chooseMe = (playerId) => {
        setMeId(playerId);
        rememberPlayer(token, playerId);
    };
    const name = data?.tournament?.name;

    useEffect(() => {
        if (name) document.title = `${name} · Tournamental`;
    }, [name]);

    if (status === 'gone') {
        return (
            <main className="public-page">
                <div className="empty">
                    <TrophyIcon size={36} />
                    <strong>This link is no longer active</strong>
                    <p>Ask the organizer for a new link.</p>
                </div>
            </main>
        );
    }

    if (!data) {
        return (
            <main className="public-page">
                {status === 'error' ? (
                    <div className="empty">
                        <strong>Could not load the tournament</strong>
                        <p>Check your connection. This page tries again on its own.</p>
                    </div>
                ) : (
                    <div className="skeleton" aria-busy="true" />
                )}
            </main>
        );
    }

    const { tournament, standings, ranking, rounds } = data;
    const dates = formatDateRange(tournament.startDate, tournament.endDate);

    return (
        <main className="public-page">
            <header className="public-header">
                <span className="final-trophy" aria-hidden="true">
                    <TrophyIcon size={28} />
                </span>
                <h1>{tournament.name}</h1>
                {dates && <p className="muted">{dates}</p>}
                <p className={`live${status === 'stale' ? ' stale' : ''}`} role="status">
                    <span className="live-dot" aria-hidden="true" />
                    {status === 'stale' ? 'Connection lost, showing the last update' : 'Live'}
                    {fetchedAt && ` · updated ${clock(fetchedAt)}`}
                </p>
            </header>

            {standings.length > 0 && <PlayerPicker standings={standings} value={meId} onChange={chooseMe} />}
            {meId !== null && standings.some((row) => row.playerId === meId) && (
                <MyGames
                    rounds={rounds}
                    playerId={meId}
                    courts={tournament.courts}
                    name={`${standings.find((row) => row.playerId === meId).firstName} ${standings.find((row) => row.playerId === meId).lastName}`}
                    standings={standings}
                />
            )}
            <p className="screen-link">
                <Link to={`/s/${token}/screen`}>Show on a big screen</Link>
            </p>

            <div className="view-tabs" role="tablist" aria-label="Ranking or rounds">
                {[['ranking', 'Ranking'], ['rounds', 'Rounds']].map(([key, label]) => (
                    <button
                        key={key}
                        role="tab"
                        aria-selected={view === key}
                        className={`view-tab${view === key ? ' active' : ''}`}
                        onClick={() => setView(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {view === 'ranking' ? (
                standings.length === 0 ? (
                    <div className="empty">
                        <strong>No players yet</strong>
                    </div>
                ) : (
                    <>
                        {hasResults(standings) && <Podium standings={standings} ranking={ranking} />}
                        <div className="card ranking-card">
                            {!hasResults(standings) && <p className="hint ranking-note">No scores yet.</p>}
                            <RankingTable standings={standings} ranking={ranking} highlightId={meId} />
                            {ranking && <ScoreHelp ranking={ranking} />}
                        </div>
                    </>
                )
            ) : (
                <ReadOnlyRounds rounds={rounds} courts={tournament.courts} meId={meId} />
            )}
        </main>
    );
}

export default SharedTournamentPage;
